import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ReactApexChart from "react-apexcharts";
import { Dropdown, Card, Spinner } from "react-bootstrap";
import { GET } from "../../../services/AxiosService";
import moment from "moment";

function Home() {
  const [user, setUser] = useState([]);
  const [performanceData, setPerformanceData] = useState([]);
  const [chartYears, setChartYears] = useState({});
  const [availableYears, setAvailableYears] = useState([]);
  const [loading, setLoading] = useState(true);

  const [activeUserData, setActiveUserData] = useState([]);
  const [yearPairs, setYearPairs] = useState([]);
  const [selectedYearPair, setSelectedYearPair] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("Total");
  const [selectedTimeSpan, setSelectedTimeSpan] = useState("monthly");

  const navigate = useNavigate();
  const nameUser = localStorage?.getItem("accessName");

  const [userTotalData, setUserTotalData] = useState([]);
  const [totalYearPairs, setTotalYearPairs] = useState([]);
  const [selectedTotalYearPair, setSelectedTotalYearPair] = useState("");
  const [selectedTotalTimeSpan, setSelectedTotalTimeSpan] = useState("monthly");

  const getFixedYearPair = (year) => {
    const currentYear = new Date().getFullYear();
    const base = currentYear + Math.floor((year - currentYear) / 2) * 2;
    return `${base} - ${base + 1}`;
  };

  const fetchUser = async () => {
    const idUser = localStorage.getItem("accessId");
    if (!idUser) {
      navigate("/login");
      return;
    }
    const res = await GET(`/user/${idUser}`);
    setUser(res?.data);
  };

  const permissions = JSON.parse(
    localStorage.getItem("DASHBOARD_DASHBOARD") || "[]"
  );
  const canViewDashboard = permissions.includes("VIEW");

  const fetchPerformance = async () => {
    setLoading(true);
    try {
      const currentYear = new Date().getFullYear();

      let startDate = moment().subtract(12, 'months').endOf('month').toISOString().split("T")[0];
      let endDate = new Date().toISOString().split("T")[0]; // "2027-12-31"

      const response = await GET("/dashboard/performance", {
        start_date: startDate,
        end_date: endDate,
      });

      const yearSet = new Set();
      response.data.forEach((item) => {
        item.data.forEach((entry) => {
          const year = new Date(entry.start_date).getFullYear();
          yearSet.add(year);
        });
      });

      const sortedYears = Array.from(yearSet).sort();
      setAvailableYears(sortedYears);

      const fallbackYear = sortedYears.includes(currentYear)
        ? currentYear.toString()
        : sortedYears[sortedYears.length - 1].toString();

      const initialYears = {};
      response.data.forEach((item) => {
        initialYears[item.id] = fallbackYear;
      });

      setChartYears(initialYears);
      setPerformanceData(response.data);
    } catch (error) {
      console.error("Error fetching performance data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveUsers = async (_startDate, _endDate) => {
    setLoading(true);
    try {
      const currentYear = new Date().getFullYear();

      let startFetchDate = moment().subtract(12, 'months').endOf('month').toISOString().split("T")[0];
      // Call the API with default date range covering possible years
      // let startFetchDate = new Date(currentYear, 0, 1).toISOString().split("T")[0]; // "2025-01-01"
      let endFetchDate = new Date().toISOString().split("T")[0]; // "2027-12-31"
      if (_startDate) {
        startFetchDate = _startDate.toISOString().split("T")[0];
      }
      if (_endDate) {
        endFetchDate = _endDate.toISOString().split("T")[0];
      }


      const response = await GET("/dashboard/user_active", {
        start_date: startFetchDate,
        end_date: endFetchDate,
        timespan: selectedTimeSpan,
      });

      // Extract available year pairs from response
      const yearSet = new Set();
      response.data.forEach((entry) => {
        const year = new Date(entry.start_date).getFullYear();
        const baseYear = currentYear + Math.ceil((year - currentYear) / 2) * 2;
        yearSet.add(`${baseYear-1} - ${baseYear}`);
      });

      const uniquePairs = Array.from(yearSet).sort();
      setYearPairs(uniquePairs);

      // Pick default pair
      const currentYearPair = getFixedYearPair(currentYear);
      const defaultPair = uniquePairs.includes(currentYearPair)
        ? currentYearPair
        : uniquePairs[0];

      if (!selectedYearPair || selectedYearPair !== defaultPair) {
        setSelectedYearPair(defaultPair);
      }

      // Filter active data for selected year pair
      const startYear = parseInt(defaultPair.split(" - ")[0]);
      const endYear = startYear + 1;
      const startDate = new Date(startYear, 0, 1);
      const endDate = new Date(endYear, 11, 31);

      const filteredData = response.data.filter((entry) => {
        const date = new Date(entry.start_date);
        return date >= startDate && date <= endDate;
      });

      setActiveUserData(filteredData);
    } catch (error) {
      console.error("Error fetching user_active:", error);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    fetchUser();
    if (canViewDashboard) {
      fetchPerformance();
    }
  }, []);

  useEffect(() => {
    if (canViewDashboard) {
      fetchActiveUsers();
    }
  }, [selectedTimeSpan]);

  const handleYearPairChange = (pair) => {
    setSelectedYearPair(pair);

    if (!canViewDashboard) return;

    const startYear = parseInt(pair.split(" - ")[0]);
    const endYear = startYear + 1;

    const startDate = new Date(startYear, 0, 1);
    const endDate = new Date(endYear, 11, 31);

    fetchActiveUsers(startDate, endDate);
  };


  const processChartData = () => {
    let labels = [];
    if (selectedTimeSpan === "weekly") {
      labels = activeUserData.map((entry) => {
        const start = new Date(entry.start_date);
        const end = new Date(entry.end_date);
        const format = (date) =>
          `${date.getDate()} ${date.toLocaleString("id-ID", { month: "short" })}`;
        return `${format(start)} - ${format(end)}`;
      });
    } else {
      labels = activeUserData.map((entry) =>
        new Date(entry.start_date).toLocaleDateString("id-ID", {
          month: "short",
          year: "numeric",
        })
      );
    }

    const awasData = activeUserData.map((entry) => entry.awas || 0);
    const tunaNetraData = activeUserData.map((entry) => entry.tuna_netra || 0);
    const totalData = awasData.map((val, i) => val + tunaNetraData[i]);

    let series = [];
    if (selectedCategory === "Total") {
      series = [
        { name: "Orang Awas", data: awasData },
        { name: "Tuna Netra", data: tunaNetraData },
        { name: "Total", data: totalData },
      ];
    } else if (selectedCategory === "Orang Awas") {
      series = [{ name: "Orang Awas", data: awasData }];
    } else {
      series = [{ name: "Tuna Netra", data: tunaNetraData }];
    }


    return { labels, series };
  };

  const { labels, series } = processChartData();

  const chartOptions = {
    chart: {
      type: "line", height: 350, toolbar: { show: false },

    },
    stroke: { curve: "smooth", width: 2 },
    xaxis: {
      categories: labels,
      labels: {
        rotate: selectedTimeSpan === "weekly" ? -30 : 0,
        style: { fontSize: "12px" },
      },
      title: {
        text:
          selectedTimeSpan === "weekly"
            ? "Minggu"
            : selectedTimeSpan === "monthly"
              ? "Bulan"
              : "Periode",
      },
    },
    yaxis: {
      title: { text: "Jumlah" },
    },
    tooltip: {
      y: { formatter: (val) => `${val} orang` },
    },
    legend: { position: "top" },
    colors: ["#FFA500", "#00E396", "#008FFB"],
  };

  const generateBarOptions = (name, categories) => ({
    chart: {
      type: "bar",
      height: 300,
      stacked: true,
      toolbar: { show: false },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "55%",
        endingShape: "rounded",
      },
    },
    stroke: {
      show: true,
      width: 1,
      colors: ["#fff"],
    },
    xaxis: {
      categories,
      labels: {
        rotate: -45,
        style: {
          fontSize: "12px",
          colors: "#000", // Black X-axis
        },
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: "#000", // Black Y-axis
        },
        formatter: (val) => val.toFixed(0),
      },
    },
    tooltip: {
      theme: "light", // White tooltip with dark text
    },
    legend: {
      position: "top",
      labels: {
        colors: "#000", // Legend text black
      },
    },
    colors: ["#795DED", "#FD6622"], // Green for "User Baru", Yellow for "User Lama"
    fill: { opacity: 1 },
    dataLabels: {
      enabled: true,
      style: {
        colors: ["#ffff"], // ✅ This sets the bar label numbers to black
        fontSize: "12px",
        fontWeight: "bold",
      },
    },
  });

  const renderPerformanceCharts = () =>
    performanceData.map((item) => {
      const selectedYear = chartYears[item.id] || "2025";

      const filteredData = item.data.filter(
        (d) => new Date(d.start_date).getFullYear().toString() === selectedYear
      );

      const weekMap = new Map();

      filteredData.forEach((entry) => {
        const start = new Date(entry.start_date);
        const end = new Date(entry.end_date);
        const weekLabel = `${start.getDate()} ${start.toLocaleString("id-ID", { month: "short" })} - ${end.getDate()} ${end.toLocaleString("id-ID", { month: "short" })}`;

        if (!weekMap.has(weekLabel)) {
          weekMap.set(weekLabel, { new_user: 0, cont_user: 0 });
        }

        const current = weekMap.get(weekLabel);
        current.new_user += entry.new_user || 0;
        current.cont_user += entry.cont_user || 0;
      });

      const categories = Array.from(weekMap.keys());
      const newUserData = Array.from(weekMap.values()).map((v) => v.new_user);
      const contUserData = Array.from(weekMap.values()).map((v) => v.cont_user);

      const series = [
        { name: "User Baru", data: newUserData },
        { name: "User Lama", data: contUserData },
      ];

      const options = generateBarOptions(item.name, categories);

      return (
        <Card key={item.id} className="mt-4">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <Card.Title>{item.name}</Card.Title>
            <Dropdown>
              <Dropdown.Toggle className="btn btn-sm btn-primary">
                {selectedYear}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {availableYears.map((year) => (
                  <Dropdown.Item
                    key={year}
                    onClick={() =>
                      setChartYears((prev) => ({
                        ...prev,
                        [item.id]: year.toString(),
                      }))
                    }
                  >
                    {year}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </Card.Header>
          <Card.Body>
            <ReactApexChart options={options} series={series} type="bar" height={300} />
          </Card.Body>
        </Card>
      );
    });



  const fetchUserTotal = async (_startDate, _endDate) => {
    setLoading(true);
    try {      
      const currentYear = new Date().getFullYear();

      let startFetchDate = moment().subtract(12, 'months').endOf('month').toISOString().split("T")[0];
      // Call the API with default date range covering possible years
      // let startFetchDate = new Date(currentYear, 0, 1).toISOString().split("T")[0]; // "2025-01-01"
      let endFetchDate = new Date().toISOString().split("T")[0]; // "2027-12-31"
      if (_startDate) {
        startFetchDate = _startDate.toISOString().split("T")[0];
      }
      if (_endDate) {
        endFetchDate = _endDate.toISOString().split("T")[0];
      }      

      const response = await GET("/dashboard/user_total", {
        start_date: startFetchDate,
        end_date: endFetchDate,
        timespan: selectedTotalTimeSpan,
      });

      // Generate 2-year pairs dynamically
      const yearSet = new Set();
      response.data.forEach((entry) => {
        const year = new Date(entry.start_date).getFullYear();
        const baseYear = currentYear + Math.ceil((year - currentYear) / 2) * 2;
        yearSet.add(`${baseYear-1} - ${baseYear}`);
      });

      const uniquePairs = Array.from(yearSet).sort();
      setTotalYearPairs(uniquePairs);

      const currentYearPair = getFixedYearPair(currentYear);
      const defaultPair = uniquePairs.includes(currentYearPair)
        ? currentYearPair
        : uniquePairs[0];

      if (!selectedTotalYearPair || selectedTotalYearPair !== defaultPair) {
        setSelectedTotalYearPair(defaultPair);
      }

      // Filter data for selected year pair
      const startYear = parseInt(defaultPair.split(" - ")[0]);
      const endYear = startYear + 1;
      const startFilterDate = new Date(startYear, 0, 1);
      const endFilterDate = new Date(endYear, 11, 31);

      const filtered = response.data.filter((entry) => {
        const date = new Date(entry.start_date);
        return date >= startFilterDate && date <= endFilterDate;
      });

      setUserTotalData(filtered);
    } catch (error) {
      console.error("Error fetching user_total:", error);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    if (canViewDashboard) {
      fetchUserTotal();
    }
  }, [selectedTotalTimeSpan]);


  const handleTotalYearPairChange = (pair) => {
    setSelectedTotalYearPair(pair);
    if (canViewDashboard) {
      fetchUserTotal();
    }
  };

  const processUserTotalChartData = () => {
    const labels = userTotalData.map((entry) => {
      const start = new Date(entry.start_date);
      const end = new Date(entry.end_date);
      const format = (date) =>
        `${date.getDate()} ${date.toLocaleString("id-ID", { month: "short" })}`;
      return `${format(start)} - ${format(end)}`;
    });

    const awasData = userTotalData.map((entry) => entry.awas || 0);
    const tunaNetraData = userTotalData.map((entry) => entry.tuna_netra || 0);
    const totalData = awasData.map((val, i) => val + tunaNetraData[i]);

    let series = [
      { name: "Orang Awas", data: awasData },
      { name: "Tuna Netra", data: tunaNetraData },
      { name: "Total", data: totalData },
    ];

    return { labels, series };

  };

  const { labels: totalLabels, series: totalSeries } = processUserTotalChartData();

  return (
    <div className="container mt-4 mx-1">
      <div className="row">
        <h2>Selamat Datang {nameUser}, di Yayasan Hikmah Mata Hati</h2>
      </div>
      {canViewDashboard && (
        <Card className="mt-4">
          <Card.Header>
            <Card.Title>User Aktif</Card.Title>
            <div className="d-flex justify-content-end mb-3 gap-2 flex-wrap">
              <Dropdown>
                <Dropdown.Toggle className="btn-sm btn-primary">
                  {selectedTimeSpan.charAt(0).toUpperCase() + selectedTimeSpan.slice(1)}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {["weekly", "monthly", "quarterly", "biannually"].map((span) => (
                    <Dropdown.Item key={span} onClick={() => setSelectedTimeSpan(span)}>
                      {span.charAt(0).toUpperCase() + span.slice(1)}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>

              <Dropdown>
                <Dropdown.Toggle className="btn-sm btn-primary mx-2">
                  {selectedYearPair || "Pilih Tahun"}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {yearPairs.map((pair) => (
                    <Dropdown.Item key={pair} onClick={() => handleYearPairChange(pair)}>
                      {pair}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </Card.Header>
          <Card.Body>


            {loading ? (
              <div className="d-flex justify-content-center align-items-center my-5">
                <Spinner animation="border" role="status" />
              </div>
            ) : (
              <ReactApexChart
                options={chartOptions}
                series={series}
                type="line"
                height={350}
              />
            )}
          </Card.Body>
        </Card>
      )}

      {canViewDashboard && (
        <Card className="mt-4">
          <Card.Header>
            <Card.Title>User Total</Card.Title>
            <div className="d-flex justify-content-end mb-3 gap-2 flex-wrap">
              <Dropdown>
                <Dropdown.Toggle className="btn-sm btn-primary">
                  {selectedTotalTimeSpan.charAt(0).toUpperCase() + selectedTotalTimeSpan.slice(1)}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {["weekly", "monthly", "quarterly", "biannually"].map((span) => (
                    <Dropdown.Item key={span} onClick={() => setSelectedTotalTimeSpan(span)}>
                      {span.charAt(0).toUpperCase() + span.slice(1)}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>

              <Dropdown>
                <Dropdown.Toggle className="btn-sm btn-primary mx-2">
                  {selectedTotalYearPair || "Pilih Tahun"}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {totalYearPairs.map((pair) => (
                    <Dropdown.Item key={pair} onClick={() => handleTotalYearPairChange(pair)}>
                      {pair}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </Card.Header>
          <Card.Body>


            {loading ? (
              <div className="d-flex justify-content-center align-items-center my-5">
                <Spinner animation="border" />
              </div>
            ) : (
              <ReactApexChart
                options={{
                  chart: {
                    type: "line", height: 350, toolbar: { show: false },

                  },
                  stroke: { curve: "smooth", width: 2 },
                  xaxis: {
                    categories: totalLabels,
                    labels: {
                      rotate: selectedTotalTimeSpan === "weekly" ? -30 : 0,
                      style: { fontSize: "12px" },
                    },
                    title: {
                      text:
                        selectedTotalTimeSpan === "weekly"
                          ? "Minggu"
                          : selectedTotalTimeSpan === "monthly"
                            ? "Bulan"
                            : "Periode",
                    },
                  },
                  yaxis: {
                    title: { text: "Jumlah" },
                  },
                  tooltip: {
                    y: { formatter: (val) => `${val} orang` },
                  },
                  legend: { position: "top" },
                  colors: ["#FFA500", "#00E396", "#008FFB"],
                }}
                series={totalSeries}
                type="line"
                height={350}
              />
            )}
          </Card.Body>
        </Card>
      )}

      {canViewDashboard ? (
        loading ? (
          <div className="text-center my-5">
            <Spinner animation="border" />
          </div>
        ) : (
          renderPerformanceCharts()
        )
      ) : null}

    </div>
  );
}

export default Home;
